import {
  CalendarConfigurationDto,
  DataBlockVariant,
  AppointmentColorSettingsRuleDto,
  DataBlockDictionaryItemDto,
  ResourceDto,
  OptionalSubtenantDictionaryRecordDto,
  PatientDto,
  ResourceType,
  PatientEgkCardData,
  FileInfoDto,
  DoctorType,
  InputTemplateType,
  DoctorDto,
  VisitTypeDto,
} from '@lmt/shared-types';
import { DictionaryInfo } from '@lmt/unicorn-shared/app/components/dictionaries/dictionaries.types';
import { PatientListConfigurationData } from '@lmt/unicorn-shared/app/components/patients/list/common/types';
import { ViewType } from 'devextreme/ui/scheduler';
import { CardData } from '@lmt/unicorn-shared/app/components/card-reader/components/CardReaderHandler';
import React, { useContext } from 'react';
import { CreateDataBlockFieldDataProp } from '../measurement-fields/CreateDataBlockField';
import { DataBlockValues } from '../measurements/common';

export interface GlobalFormsContext {
  closeForm(formId: number): void;
  tryCloseForm(formId: number): void;
  openPatientCreating(
    subtenantId?: number,
    doctorId?: number,
    expandedOnly?: boolean,
    defaultValues?: any,
    closeExistingForms?: boolean,
  ): Promise<PatientDto | undefined>;
  openPatientEditing(patientId: number): void;
  openVisitCreating(
    selectedInterval?: Interval,
    assignedResources?: number[],
    patientId?: number,
    caseId?: number,
    visitTypeId?: number | null,
    timeSlotTimeStepInMinutes?: number,
    calendarType?: ViewType,
    defaultVisitStartTimeInSeconds?: number,
  ): void;
  openVisitEditing(
    visitId: number,
    isWeekView?: boolean,
    defaultVisitStartTimeInSeconds?: number,
  ): void;
  openEventCreating(selectedInterval?: Interval, patientId?: number): void;
  openUserCreating(): void;
  openUserEditing(userId: string): void;
  setUserPassword(userId: string): void;
  changeOwnPassword(): void;
  changeLanguage(): void;
  openRoleCreating(): void;
  openRoleEditing(roleId: number, isDefault?: boolean): void;
  openSubtenantCreating(): void;
  openSubtenantEditing(id: number): void;
  importFromMiniscreen(): void;
  openDoctorCreating(
    doctorType: DoctorType,
    showResourceInfo?: boolean,
  ): Promise<DoctorDto | undefined>;
  openDoctorEditing(
    doctorType: DoctorType,
    doctorId: number,
    showResourceInfo?: boolean,
  ): void;
  openDataBlockTypeCreation(
    variant: DataBlockVariant,
    onSuccess?: (dataBlockTypeId: number) => void,
  ): void;
  openDataBlockFieldCreation(
    dataBlockTypeId: number,
    data?: CreateDataBlockFieldDataProp,
  ): void;
  openDataBlockFieldEdit(dataBlockFieldId: number): void;
  openDataBlock(
    dataBlockId: number,
    dataBlockTypeId: number,
    patientId: number,
    dataBlockTypeName?: string,
  ): void;
  openDataBlockCreation(
    dataBlockTypeId: number,
    patientId: number,
    caseId: number,
    visitId?: number,
    dataBlockTypeName?: string,
  ): void;
  openAppointmentColorSettings(rule?: AppointmentColorSettingsRuleDto): void;
  openHL7Filter(filterId?: number): void;
  openResourceCreating(type: ResourceType, title?: string): void;
  openResourceEditing(resourceToEdit: ResourceDto, title?: string): void;
  importDocument(blockId: number): void;
  importMeasurementFiles(): void;
  importGDTFile(): void;
  editMergeField(mergeFieldId: number): void;
  addMergeField(): void;
  openDictionaryRecordCreating(dictionary: DictionaryInfo): void;
  openDictionaryRecordEditing(
    dictionary: DictionaryInfo,
    data: OptionalSubtenantDictionaryRecordDto,
  ): void;
  openConfigureCustomDictionary(dictionaryId: number): void;
  openCreateCustomDictionaryItem(
    dictionaryId: number,
    onClose?: () => void,
  ): Promise<DataBlockDictionaryItemDto | undefined>;
  openEditCustomDictionaryItem(
    dictionaryId: number,
    item: DataBlockDictionaryItemDto,
  ): void;
  openCalendarConfigurationCreating(): Promise<
    CalendarConfigurationDto | undefined
  >;
  openCalendarConfigurationEditing(
    configurationId: number,
  ): Promise<CalendarConfigurationDto | undefined>;
  openPatientListConfigurationCreating(
    values?: Partial<PatientListConfigurationData>,
    resolve?: (newConfigurationId: number) => void,
  ): void;
  openPatientListConfigurationEditing(
    configurationId?: number,
    values?: Partial<PatientListConfigurationData>,
    resolve?: (newConfigurationId: number) => void,
  ): void;
  openMergePair(
    firstPatientId?: number,
    secondPatientId?: number,
    patientDuplicatePairId?: number,
  ): void;
  openGDTMappings(): void;
  openGDTSending(blockId: number): void;
  openSelectVisits(caseId: number): Promise<number[] | null>;
  openAbsenceCreating(selectedInterval?: Interval): void;
  openAbsenceEditing(absenceId: number): void;
  openNoteCreating(): void;
  openNoteEditing(noteId: number): void;
  openFileVersionHistory(
    groupId: string,
    expanded?: boolean,
    // if `undefined` or returns `false`, then `defaultOnClick` (using `useFileCallbacks`) is called
    onVersionClick?: (file: FileInfoDto) => void,
    highlightCurrentVersion?: boolean,
  ): void;
  openDataBlockStatisticEditing(
    dataBlockId: number,
    dataBlockTypeId: number,
    dataBlockTypeName?: string,
  ): void;
  openDataBlockStatisticCreating(
    dataBlockTypeId: number,
    patientId: number,
    caseId: number,
    visitId?: number,
  ): Promise<DataBlockValues | null>;
  openVisitTypeCreating(): Promise<VisitTypeDto | undefined>;
  openVisitTypeEditing(id: number): void;
  openInputTemplatesEditing(
    templateType: InputTemplateType,
    fieldId: number | null,
    isNumber: boolean,
    subtenantId?: number,
    title?: string,
  ): void;
  openReImportMeasurements(processId: string): void;
  openMovePatients(processId: string): Promise<boolean | undefined>;
  openMergePatientWithCardData(
    patient: PatientDto,
    cardData: CardData,
    closeExistingForms?: boolean,
  ): void;
  openMergeInsuranceCardData(
    healthInsuranceCompany: DataBlockDictionaryItemDto,
    cardData: CardData,
  ): Promise<void>;
  openCopyDataBlockValue(
    patientId: number,
    typeId: number,
    fieldId: number,
    config: string,
    typeName?: string,
  ): Promise<string | undefined>;
  openCodePrefixCreating(): Promise<string | undefined>;
  openSendTestMessage(subtenantId: number): void;
}

export const FormsContext = React.createContext<GlobalFormsContext>({
  closeForm: () => undefined,
  tryCloseForm: () => undefined,
  openPatientCreating: () => Promise.resolve(undefined),
  openPatientEditing: () => undefined,
  changeOwnPassword: () => undefined,
  changeLanguage: () => undefined,
  setUserPassword: () => undefined,
  openVisitCreating: () => undefined,
  openVisitEditing: () => undefined,
  openEventCreating: () => undefined,
  openUserCreating: () => undefined,
  openUserEditing: () => undefined,
  openRoleCreating: () => undefined,
  openRoleEditing: () => undefined,
  openSubtenantCreating: () => undefined,
  openSubtenantEditing: () => undefined,
  importFromMiniscreen: () => undefined,
  openDoctorCreating: () => Promise.resolve(undefined),
  openDoctorEditing: () => undefined,
  openDataBlockTypeCreation: () => undefined,
  openDataBlockFieldCreation: () => undefined,
  openDataBlockFieldEdit: () => undefined,
  openDataBlock: () => undefined,
  openDataBlockCreation: () => undefined,
  openAppointmentColorSettings: () => undefined,
  openHL7Filter: () => undefined,
  openResourceCreating: () => undefined,
  openResourceEditing: () => undefined,
  importDocument: () => undefined,
  importMeasurementFiles: () => undefined,
  importGDTFile: () => undefined,
  editMergeField: () => undefined,
  addMergeField: () => undefined,
  openDictionaryRecordCreating: () => undefined,
  openDictionaryRecordEditing: () => undefined,
  openConfigureCustomDictionary: () => undefined,
  openCreateCustomDictionaryItem: () => Promise.resolve(undefined),
  openEditCustomDictionaryItem: () => undefined,
  openCalendarConfigurationCreating: () => Promise.resolve(undefined),
  openCalendarConfigurationEditing: () => Promise.resolve(undefined),
  openPatientListConfigurationCreating: () => undefined,
  openPatientListConfigurationEditing: () => undefined,
  openMergePair: () => undefined,
  openGDTMappings: () => undefined,
  openGDTSending: () => undefined,
  openSelectVisits: () => undefined!,
  openAbsenceCreating: () => undefined,
  openAbsenceEditing: () => undefined,
  openNoteCreating: () => undefined,
  openNoteEditing: () => undefined,
  openFileVersionHistory: () => undefined,
  openDataBlockStatisticEditing: () => undefined,
  openDataBlockStatisticCreating: () => undefined!,
  openVisitTypeCreating: () => Promise.resolve(undefined),
  openVisitTypeEditing: (id: number) => undefined,
  openInputTemplatesEditing: () => undefined,
  openReImportMeasurements: () => undefined,
  openMovePatients: () => Promise.resolve(undefined),
  openMergePatientWithCardData: () => undefined,
  openMergeInsuranceCardData: () => Promise.resolve(undefined),
  openCopyDataBlockValue: () => undefined!,
  openCodePrefixCreating: () => undefined!,
  openSendTestMessage: () => undefined,
});

export function useGlobalForms(): GlobalFormsContext {
  return useContext(FormsContext);
}
